import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

export interface Device {
  id: string;
  imei: string;
  label: string | null;
  status: 'unclaimed' | 'active' | 'disabled';
  isVirtual: boolean;
  claimCode: string;
  lastSeenAt: string | null;
  createdAt: string;
  claimedAt: string | null;
  tenantId: string | null;
  tenantName: string | null;
  deviceProfileId: string;
  profileName: string;
  profileVendor: string;
  // Sin coordenadas el device queda invisible en el panel del cliente
  // (useSensors filtra los que no tienen lat/lng).
  lat: number | null;
  lng: number | null;
  // metadata.portMap: puerto físico del sensor → profundidad. Sin esto la
  // telemetría se guarda con depth_cm null y la vista de suelo no la muestra.
  metadata: { portMap?: Record<string, { depthCm: number }> } | null;
}

interface Tenant {
  id: string;
  name: string;
}

interface DeviceProfile {
  id: string;
  name: string;
  vendor: string;
}

const th: React.CSSProperties = { padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.06em' };
const td: React.CSSProperties = { padding: '12px 16px' };

export default function DevicesPage() {
  const { t, i18n } = useTranslation();
  const [devices, setDevices]         = useState<Device[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [assignDevice, setAssignDevice] = useState<Device | null>(null);
  const [showAddForm, setShowAddForm]   = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin/devices');
      setDevices(data);
    } catch {
      setError(t('devices.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const unclaimed = devices.filter(d => d.status === 'unclaimed');
  const rest = devices.filter(d => d.status !== 'unclaimed');

  // Agrupado por cliente para que sea manejable con muchos tenants — un
  // device asignado siempre debería traer tenantName, pero si su tenant
  // fue borrado (FK onDelete: 'set null') cae al mismo bucket "Unassigned"
  // que usa DeviceTable para tenantName null.
  const byClient = new Map<string, Device[]>();
  for (const d of rest) {
    const key = d.tenantName ?? t('devices.unassigned');
    if (!byClient.has(key)) byClient.set(key, []);
    byClient.get(key)!.push(d);
  }
  const clientGroups = [...byClient.entries()].sort((a, b) => a[0].localeCompare(b[0]));

  return (
    <div className="page">
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>{t('devices.title')}</h1>
          <p style={{ fontSize: 13, color: 'var(--gray)' }}>{t('devices.count', { count: devices.length })}</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          style={{ padding: '8px 18px', background: 'var(--dark)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
        >
          {t('devices.addNew')}
        </button>
      </div>

      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ color: 'var(--gray)', fontSize: 13 }}>{t('devices.loading')}</div>
      ) : (
        <>
          {unclaimed.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <SectionHeader label={t('devices.unclaimed')} count={unclaimed.length} accent />
              <DeviceTable devices={unclaimed} onAssign={setAssignDevice} t={t} i18n={i18n} />
            </div>
          )}

          <div>
            <SectionHeader label={t('devices.active')} count={rest.length} />
            {rest.length === 0 ? (
              <div style={{ padding: '20px 0', color: 'var(--gray)', fontSize: 13 }}>{t('devices.noneActive')}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {clientGroups.map(([clientName, clientDevices]) => (
                  <div key={clientName}>
                    <ClientGroupHeader label={clientName} count={clientDevices.length} />
                    <DeviceTable devices={clientDevices} onAssign={setAssignDevice} t={t} i18n={i18n} showTenantColumn={false} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {assignDevice && (
        <AssignDeviceModal
          device={assignDevice}
          onClose={() => setAssignDevice(null)}
          onSaved={() => { setAssignDevice(null); load(); }}
        />
      )}

      {showAddForm && (
        <AddDeviceModal
          onClose={() => setShowAddForm(false)}
          onSaved={() => { setShowAddForm(false); load(); }}
        />
      )}
    </div>
  );
}

function SectionHeader({ label, count, accent }: { label: string; count: number; accent?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: accent ? 'var(--amber)' : 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {label}
      </div>
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 99,
        background: accent ? 'var(--amber-bg)' : '#f5f5f3',
        color: accent ? 'var(--amber)' : 'var(--gray)',
      }}>
        {count}
      </span>
    </div>
  );
}

function ClientGroupHeader({ label, count }: { label: string; count: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <span style={{ fontSize: 13 }}>🏢</span>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
      <span style={{ fontSize: 11, color: 'var(--gray)' }}>({count})</span>
    </div>
  );
}

export function DeviceTable({ devices, onAssign, t, i18n, showTenantColumn = true }: {
  devices: Device[]; onAssign: (d: Device) => void; t: any; i18n: any; showTenantColumn?: boolean;
}) {
  return (
    <div style={{ background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
            <th style={th}>{t('devices.columns.imei')}</th>
            <th style={th}>{t('devices.columns.profile')}</th>
            {showTenantColumn && <th style={th}>{t('devices.columns.tenant')}</th>}
            <th style={th}>{t('devices.columns.location')}</th>
            <th style={th}>{t('devices.columns.lastSeen')}</th>
            <th style={th}>{t('devices.columns.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {devices.map((d, i) => (
            <tr key={d.id} style={{ borderBottom: i < devices.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <td style={td}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontWeight: 500, fontSize: 13 }}>{d.imei}</span>
                  {d.isVirtual && (
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 99, background: '#f5f5f3', color: 'var(--gray)' }}>
                      {t('devices.virtual')}
                    </span>
                  )}
                </div>
                {d.label && <div style={{ fontSize: 11, color: 'var(--gray)' }}>{d.label}</div>}
              </td>
              <td style={td}>
                <div style={{ fontSize: 12 }}>{d.profileName}</div>
                <div style={{ fontSize: 10, color: 'var(--gray)' }}>{d.profileVendor}</div>
              </td>
              {showTenantColumn && (
                <td style={td}>
                  {d.tenantName
                    ? <span style={{ fontSize: 13 }}>{d.tenantName}</span>
                    : <span style={{ fontSize: 12, color: 'var(--gray)' }}>{t('devices.unassigned')}</span>}
                </td>
              )}
              {/* Un device sin lat/lng no aparece en ningún mapa del panel del
                  tenant (useSensors lo filtra). Se marca en rojo porque es un
                  problema accionable, no un dato informativo. */}
              <td style={td}>
                {d.lat != null && d.lng != null ? (
                  <span style={{ fontSize: 11, color: 'var(--gray)', fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' }}>
                    {Number(d.lat).toFixed(4)}, {Number(d.lng).toFixed(4)}
                  </span>
                ) : (
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: 'var(--red-bg, #fdecea)', color: 'var(--red, #c0392b)' }}>
                    {t('devices.noLocation')}
                  </span>
                )}
              </td>
              <td style={{ ...td, fontSize: 12, color: 'var(--gray)' }}>
                {d.lastSeenAt
                  ? new Date(d.lastSeenAt).toLocaleString(i18n.language.startsWith('es') ? 'es-MX' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                  : t('devices.neverSeen')}
              </td>
              <td style={td}>
                {/* Antes este botón solo salía con status 'unclaimed', así que
                    un device ya asignado no se podía corregir desde la UI —
                    si se dio de alta sin coordenadas quedaba invisible en el
                    panel del cliente y sin forma de arreglarlo. */}
                <button
                  onClick={() => onAssign(d)}
                  style={{ fontSize: 11, padding: '5px 12px', borderRadius: 6, border: '1px solid var(--green)', background: 'var(--green-bg)', color: 'var(--green-dk)', cursor: 'pointer', fontWeight: 600 }}
                >
                  {d.status === 'unclaimed' ? t('devices.assign') : t('devices.edit')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AssignDeviceModal({ device, onClose, onSaved }: { device: Device; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [tenants, setTenants]   = useState<Tenant[]>([]);
  const [profiles, setProfiles] = useState<DeviceProfile[]>([]);
  // Precargados con lo que el device ya tiene: este mismo modal ahora también
  // sirve para EDITAR uno ya asignado, y si los campos salieran vacíos, guardar
  // sin tocarlos borraría el profile o dejaría las coordenadas a medias.
  const isEdit = device.status !== 'unclaimed';
  const [tenantId, setTenantId] = useState(device.tenantId ?? '');
  const [deviceProfileId, setDeviceProfileId] = useState(device.deviceProfileId ?? '');
  const [label, setLabel]       = useState(device.label ?? '');
  const [lat, setLat]           = useState(device.lat != null ? String(device.lat) : '');
  const [lng, setLng]           = useState(device.lng != null ? String(device.lng) : '');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  // El mapeo se edita como filas puerto/profundidad porque así lo entrega el
  // proveedor ("puerto 1 = 10 cm"), no como el objeto anidado que se guarda.
  const [ports, setPorts] = useState<Array<{ port: string; depth: string }>>(() => {
    const pm = device.metadata?.portMap ?? {};
    const rows = Object.entries(pm).map(([port, v]) => ({ port, depth: String(v.depthCm) }));
    return rows.length > 0 ? rows : [{ port: '', depth: '' }];
  });
  const setPort = (i: number, patch: Partial<{ port: string; depth: string }>) =>
    setPorts(rows => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  // Nada de .catch silencioso acá: si esto falla, el <select> queda vacío y
  // parece "no hay nada dado de alta" cuando en realidad hubo un error de API
  // — pasó con /device-profiles y un 400 de rls.ts (ver el comentario en
  // apps/core/src/modules/device-profiles/routes.ts).
  useEffect(() => {
    api.get('/admin/tenants').then(({ data }) => setTenants(data))
      .catch(() => setError(t('devices.loadError')));
    api.get('/device-profiles').then(({ data }) => setProfiles(data))
      .catch(() => setError(t('devices.profilesLoadError')));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      // Solo se manda portMap si hay al menos una fila completa. Mandar {}
      // borraría un mapeo ya guardado sin que el usuario lo pidiera.
      const portMap: Record<string, { depthCm: number }> = {};
      for (const r of ports) {
        if (r.port.trim() && r.depth.trim()) portMap[r.port.trim()] = { depthCm: Number(r.depth) };
      }

      await api.patch(`/admin/devices/${device.id}/assign`, {
        tenantId,
        ...(deviceProfileId ? { deviceProfileId } : {}),
        // Siempre se mandan, con null si el campo quedó vacío: omitirlos dejaba
        // intacto el valor anterior y no había forma de BORRAR unas coordenadas
        // desde la UI. Importa al mover un device de un cliente a otro — se
        // llevaba las coordenadas del cliente anterior.
        label: label.trim() || null,
        lat: lat.trim() ? Number(lat) : null,
        lng: lng.trim() ? Number(lng) : null,
        ...(Object.keys(portMap).length > 0 ? { portMap } : {}),
      });
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.error ?? t('devices.assignModal.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'var(--white)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>
          {isEdit ? t('devices.editModal.title') : t('devices.assignModal.title')}
        </h2>
        <p style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 20 }}>{device.imei}</p>

        {/* Aviso accionable: sin coordenadas el device no sale en el panel del
            cliente, y hasta ahora eso no se explicaba en ningún lado. */}
        {(device.lat == null || device.lng == null) && (
          <div style={{ marginBottom: 16, padding: '10px 12px', borderRadius: 8, background: 'var(--red-bg, #fdecea)', color: 'var(--red, #c0392b)', fontSize: 12, lineHeight: 1.5 }}>
            {t('devices.missingLocationWarning')}
          </div>
        )}

        {error && <div className="error-message" style={{ marginBottom: 14 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="field">
            <label>{t('devices.assignModal.tenant')}</label>
            <select
              value={tenantId}
              onChange={e => setTenantId(e.target.value)}
              required
              style={{ width: '100%', padding: '10px 13px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, outline: 'none' }}
            >
              <option value="" disabled>{t('devices.assignModal.selectTenant')}</option>
              {tenants.map(tn => <option key={tn.id} value={tn.id}>{tn.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>{t('devices.assignModal.profile')}</label>
            <select
              value={deviceProfileId}
              onChange={e => setDeviceProfileId(e.target.value)}
              style={{ width: '100%', padding: '10px 13px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, outline: 'none' }}
            >
              <option value="">{t('devices.assignModal.keepProfile', { profile: device.profileName })}</option>
              {profiles.map(p => <option key={p.id} value={p.id}>{p.name} ({p.vendor})</option>)}
            </select>
          </div>
          {/* Un device auto-registrado por MQTT llega sin nombre: la ingesta
              solo conoce el IMEI. Sin este campo no había forma de ponérselo
              desde el panel, y se quedaba mostrándose como el IMEI crudo tanto
              acá como en MQTT Logs y en el panel del cliente. */}
          <div className="field">
            <label>{t('devices.assignModal.label')}</label>
            <input
              value={label}
              onChange={e => setLabel(e.target.value)}
              maxLength={255}
              placeholder={t('devices.assignModal.labelPlaceholder')}
            />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="field" style={{ flex: 1 }}>
              <label>{t('devices.assignModal.lat')}</label>
              <input type="number" step="any" min={-90} max={90} value={lat} onChange={e => setLat(e.target.value)} placeholder={t('devices.assignModal.latPlaceholder')} />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>{t('devices.assignModal.lng')}</label>
              <input type="number" step="any" min={-180} max={180} value={lng} onChange={e => setLng(e.target.value)} placeholder={t('devices.assignModal.lngPlaceholder')} />
            </div>
          </div>

          {/* Mapeo puerto→profundidad. Se edita como filas porque así lo
              entrega el proveedor ("puerto 1 = 10 cm"). Sin esto la telemetría
              entra pero se guarda sin profundidad y la vista de suelo queda
              vacía — ver el comentario en assignDeviceBody (admin/routes.ts). */}
          <div className="field" style={{ marginTop: 4 }}>
            <label>{t('devices.portMap.title')}</label>
            <p style={{ fontSize: 11, color: 'var(--gray)', margin: '2px 0 8px', lineHeight: 1.5 }}>
              {t('devices.portMap.help')}
            </p>
            {ports.map((row, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <input
                  style={{ flex: 1 }} type="number" min={0} value={row.port}
                  onChange={e => setPort(i, { port: e.target.value })}
                  placeholder={t('devices.portMap.portPlaceholder')}
                />
                <span style={{ fontSize: 12, color: 'var(--gray)', flexShrink: 0 }}>&#8594;</span>
                <input
                  style={{ flex: 1 }} type="number" min={0} max={1000} value={row.depth}
                  onChange={e => setPort(i, { depth: e.target.value })}
                  placeholder={t('devices.portMap.depthPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setPorts(rows => rows.length > 1 ? rows.filter((_, j) => j !== i) : [{ port: '', depth: '' }])}
                  title={t('devices.portMap.remove')}
                  style={{ flexShrink: 0, width: 28, height: 28, border: '1px solid var(--border)', borderRadius: 6, background: 'transparent', color: 'var(--gray)', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}
                >&times;</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setPorts(rows => [...rows, { port: '', depth: '' }])}
                style={{ fontSize: 11, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--gray)', cursor: 'pointer' }}
              >
                {t('devices.portMap.add')}
              </button>
              {/* Atajo para el layout más común de estas sondas: un sensor por
                  cada 10 cm. Evita capturar 7 filas a mano, que es donde se
                  cuelan los errores de dedo. */}
              <button
                type="button"
                onClick={() => setPorts(Array.from({ length: 7 }, (_, i) => ({ port: String(i + 1), depth: String((i + 1) * 10) })))}
                style={{ fontSize: 11, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--gray)', cursor: 'pointer' }}
              >
                {t('devices.portMap.quickFill')}
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 10, border: '1px solid var(--border)', borderRadius: 8, background: 'transparent', fontSize: 13, cursor: 'pointer' }}>
              {t('devices.assignModal.cancel')}
            </button>
            <button type="submit" disabled={saving || !tenantId} className="primary-button" style={{ flex: 1 }}>
              {saving
                ? t('devices.assignModal.saving')
                : isEdit ? t('devices.editModal.save') : t('devices.assignModal.assign')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Add Device ───────────────────────────────────────────────────
// Da de alta un device a mano (POST /api/v1/devices, ya existía en el
// backend sin ningún formulario que lo llamara). Útil para pre-registrar
// hardware nuevo por IMEI ANTES de que se conecte por MQTT: la ingesta
// (apps/ingestion) resuelve el device por IMEI primero y solo auto-registra
// como unclaimed si no lo encuentra — si ya existe con tenant asignado,
// la primera telemetría real cae directo al tenant correcto, sin pasar por
// la cola de "Unassigned".
function AddDeviceModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [tenants, setTenants]   = useState<Tenant[]>([]);
  const [profiles, setProfiles] = useState<DeviceProfile[]>([]);
  const [imei, setImei]         = useState('');
  const [deviceProfileId, setDeviceProfileId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [label, setLabel]       = useState('');
  const [lat, setLat]           = useState('');
  const [lng, setLng]           = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  // Nada de .catch silencioso acá: si esto falla, el <select> queda vacío y
  // parece "no hay nada dado de alta" cuando en realidad hubo un error de API
  // — pasó con /device-profiles y un 400 de rls.ts (ver el comentario en
  // apps/core/src/modules/device-profiles/routes.ts).
  useEffect(() => {
    api.get('/admin/tenants').then(({ data }) => setTenants(data))
      .catch(() => setError(t('devices.loadError')));
    api.get('/device-profiles').then(({ data }) => setProfiles(data))
      .catch(() => setError(t('devices.profilesLoadError')));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/devices', {
        imei: imei.trim(),
        deviceProfileId,
        ...(tenantId ? { tenantId } : {}),
        ...(label.trim() ? { label: label.trim() } : {}),
        ...(lat.trim() ? { lat: Number(lat) } : {}),
        ...(lng.trim() ? { lng: Number(lng) } : {}),
      });
      onSaved();
    } catch (err: any) {
      setError(err.response?.status === 409 ? t('devices.addModal.duplicateError') : (err.response?.data?.error ?? t('devices.addModal.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'var(--white)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 20 }}>{t('devices.addModal.title')}</h2>

        {error && <div className="error-message" style={{ marginBottom: 14 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="field">
            <label>{t('devices.addModal.imei')}</label>
            <input value={imei} onChange={e => setImei(e.target.value)} required placeholder={t('devices.addModal.imeiPlaceholder')} />
          </div>
          <div className="field">
            <label>{t('devices.addModal.profile')}</label>
            <select
              value={deviceProfileId}
              onChange={e => setDeviceProfileId(e.target.value)}
              required
              style={{ width: '100%', padding: '10px 13px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, outline: 'none' }}
            >
              <option value="" disabled>{t('devices.addModal.selectProfile')}</option>
              {profiles.map(p => <option key={p.id} value={p.id}>{p.name} ({p.vendor})</option>)}
            </select>
          </div>
          <div className="field">
            <label>{t('devices.addModal.tenant')}</label>
            <select
              value={tenantId}
              onChange={e => setTenantId(e.target.value)}
              style={{ width: '100%', padding: '10px 13px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, outline: 'none' }}
            >
              <option value="">{t('devices.addModal.leaveUnassigned')}</option>
              {tenants.map(tn => <option key={tn.id} value={tn.id}>{tn.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>{t('devices.addModal.label')}</label>
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder={t('devices.addModal.labelPlaceholder')} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="field" style={{ flex: 1 }}>
              <label>{t('devices.assignModal.lat')}</label>
              <input type="number" step="any" min={-90} max={90} value={lat} onChange={e => setLat(e.target.value)} placeholder={t('devices.assignModal.latPlaceholder')} />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>{t('devices.assignModal.lng')}</label>
              <input type="number" step="any" min={-180} max={180} value={lng} onChange={e => setLng(e.target.value)} placeholder={t('devices.assignModal.lngPlaceholder')} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 10, border: '1px solid var(--border)', borderRadius: 8, background: 'transparent', fontSize: 13, cursor: 'pointer' }}>
              {t('devices.assignModal.cancel')}
            </button>
            <button type="submit" disabled={saving || !imei.trim() || !deviceProfileId} className="primary-button" style={{ flex: 1 }}>
              {saving ? t('devices.addModal.adding') : t('devices.addModal.add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
